/** zh-CN catalogue `timing` (W3-09; English: src/features/timing/timing.messages.ts; terms: docs/i18n/glossary.md). */
import type { MessageCatalog } from '../../core/util/types';

const catalog: MessageCatalog = {
  ns: 'timing',
  locale: 'zh-CN',
  messages: {
    panelLeft: '速度与第一拍',
    panelMain: '曲目总览',
    panelBottom: '凭耳朵检查',
    noProject: '打开一个项目以设置其速度和第一拍。',
    noSongTitle: '此谱面还没有曲目',
    noSongBody: '向项目添加一个曲目文件，即可设置其速度和第一拍。',
    analysing: '正在分析曲目',
    analysingTask:
      '{task, select, decode {正在解码曲目} peaks {正在读取波形} onsets {正在寻找节拍} tempo {正在检测速度} offset {正在寻找第一拍} other {正在分析曲目}}',
    waitingForAudio: '正在等待曲目加载完成',
    loadSong: '加载曲目',
    analysisFailed: '无法分析曲目：{error}',
    notDecoded:
      '此曲目直接从文件播放（它非常长，或浏览器无法解码），因此无法在此分析。请通过敲击设置速度，并凭耳朵检查第一拍。',
    noDecoder: '此浏览器无法解码曲目以进行分析。请通过敲击设置速度，并凭耳朵检查第一拍。',
    decodeFailed:
      '浏览器无法解码此曲目文件以进行分析。请尝试将其转换为 MP3 或 OGG，或通过敲击设置速度，并凭耳朵检查第一拍。',
    retry: '重试',
    confirmedYes: '时间设置已确认',
    confirmedNo: '时间设置尚未确认',
    confirmedNoHelp: '用下方的节拍声试听。节拍声落在拍子上时，确认时间设置。',
    confirmAction: '确认时间设置',
    unconfirmAction: '标记为未确认',
    sectionTempo: '速度',
    bpmLabel: '曲目速度（BPM）',
    bpmHelp: '方向键每次改变 0.1，按住 Ctrl 为 0.01，按住 Shift 为 1。',
    bpmInvalid: '速度是大于 0 的数字，例如 174 或 128.5。',
    bpmDown: '减慢 0.1 BPM（Ctrl 0.01，Shift 1）',
    bpmUp: '加快 0.1 BPM（Ctrl 0.01，Shift 1）',
    bpmMultiple: '此谱面有速度变化。此输入框设置第一个速度；其他速度请在表格中编辑。',
    detect: '检测',
    detectTempoTip: '自动寻找曲目的速度',
    detecting: '正在检测',
    detectAgain: '重新检测',
    candidatesLabel: '候选速度',
    candidate: '{bpm, number, 0.0} BPM',
    strength: '{level, select, strong {强} medium {中} other {弱}}',
    strengthTip: '此速度的节拍与曲目的吻合程度（{pct, number, integer}%）',
    useCandidate: '使用',
    useCandidateLabel: '使用 {bpm, number, 0.0} BPM',
    inUse: '使用中',
    noCandidates: '没有找到明确的节拍。请改为敲击得出速度。',
    octaveHelp: '检测可能选中实际速度的一半或两倍。请在此修正：',
    times2: 'x2',
    div2: '/2',
    times15: 'x1.5',
    times2Label: '速度加倍',
    div2Label: '速度减半',
    times15Label: '速度乘以 1.5',
    div15Label: '速度除以 1.5',
    tap: '敲击',
    tapTip: '跟着节拍敲击（B 键）',
    tapIdle: '请跟着节拍至少敲击 4 次。',
    tapCounting: '{count, plural, other {# 次敲击}}：继续',
    tapResult: '{count, plural, other {# 次敲击}}：{bpm, number, 0.0} BPM',
    tapSpread: '波动 ±{spread, number, 0.0}',
    tapUse: '使用',
    tapUseLabel: '使用敲击得出的速度（{bpm, number, 0.0} BPM）',
    tapReset: '重置敲击',
    tapResetHint: '2 秒内没有敲击时会自动重置。',
    sectionFirstBeat: '第一拍',
    beatZeroAt: '第 0 拍出现在 {sec, number, 0.000} 秒',
    beatZeroHelp: '谱面偏移：第一拍在曲目中的位置。',
    offsetEarlier: '第 0 拍提前 1 毫秒（Shift 10 毫秒）',
    offsetLater: '第 0 拍推迟 1 毫秒（Shift 10 毫秒）',
    offsetValueLabel: '偏移（毫秒）',
    offsetUnit: '毫秒',
    detectOffsetTip: '自动将节拍网格与曲目对齐',
    offsetConfidence: '置信度 {pct, number, integer}%',
    offsetDetected: '第 0 拍已移到 {sec, number, 0.000} 秒（置信度 {pct, number, integer}%）。',
    offsetLowConfidence:
      '第 0 拍已移到 {sec, number, 0.000} 秒，但置信度较低（{pct, number, integer}%）。请凭耳朵检查。',
    offsetNotFound: '在此曲目中找不到第一拍。请改为在总览中单击一个强拍。',
    phiraAdjust: 'Phira 谱面单独调整',
    phiraAdjustValue: '{sign}{sec, number, 0.000} 秒',
    phiraAdjustHelp:
      '此谱面包在 info.yml 中有自己的偏移。Phira 会将其叠加到谱面偏移上。将其并入可保持时间不变，并移除这项额外设置。',
    foldIn: '并入谱面偏移',
    folded: '谱面单独调整现已并入谱面偏移。',
    sectionTempoChanges: '速度变化',
    colBeat: '拍',
    colBpm: 'BPM',
    colMeter: '每小节拍数',
    colActions: '操作',
    rowPinned: '第一个速度点固定在第 0 拍。只能修改它的速度。',
    bpmRowInheritedLabel: '第 {beat} 拍的速度（与之前相同）',
    meterInheritedLabel: '第 {beat} 拍的每小节拍数（与之前相同）',
    deleteRow: '删除此变化',
    deleteRowLabel: '删除第 {beat} 拍的变化',
    meterLabel: '第 {beat} 拍的每小节拍数',
    bpmRowLabel: '第 {beat} 拍的速度',
    beatRowLabel: '变化所在的拍',
    addAtPlayhead: '在播放头处添加',
    addAtPlayheadTip: '以当前速度在播放头处添加速度变化',
    addMeter: '拍号变化',
    addMeterTip: '从播放头起修改每小节拍数',
    insertBeats: '插入拍',
    removeRange: '移除范围',
    insertAt: '在拍',
    insertLength: '要插入的拍数',
    insertGo: '插入',
    inserted: '已插入 {beats} 拍；{count, plural, other {# 个项目}}已后移。',
    removeFrom: '从拍',
    removeTo: '到拍',
    removeGo: '移除',
    removed: '已移除该范围：删除了 {removed, plural, other {# 个音符}}，{moved, plural, other {# 个项目}}已前移。',
    rangeInvalid: '结束必须在开始之后。',
    beatInvalid: '这不是拍数。请输入类似 16、16:1/2、16.5 或 23.5s（以秒为单位的时间）的值。',
    meterInvalid: '每小节拍数是大于 0 且不超过 64 的数字，例如 4、3 或 3.5（用于 7/8）。',
    policyLabel: '速度变化时',
    policyKeepBeats: '音符保持拍数',
    policyKeepTime: '音符保持时间',
    policyKeepBeatsHelp: '与 RPE 相同：之后的音符保持在原来的拍上，在时间上移动。',
    policyKeepTimeHelp: '之后的音符保持在听到的位置；重新计算其拍数。',
    retimed:
      '已重新计时 {retimed, plural, other {# 个项目}}{snapped, plural, =0 {} other {，# 个已吸附到 1/1920 拍}}。',
    meterExists: '第 {beat} 拍已有一个拍号变化。',
    otherDifficulties: '{count, plural, other {这首曲目的其他 # 个难度仍使用旧的速度表。}}',
    meterBefore: '拍号变化会开始新的小节：小节线、节拍器重音和逐小节视图都会随之改变。',
    sectionMetronome: '节拍器',
    metronomeOn: '播放时节拍器发声',
    metronomeVolume: '节拍器音量',
    volumePercent: '{value, number, percent}',
    metronomePreview: '播放一声节拍',
    sectionSongInfo: '曲目信息',
    infoFile: '文件',
    infoDuration: '时长',
    infoFormat: '格式',
    infoSampleRate: '采样率',
    infoChannels: '声道',
    infoSampleRateValue: '{khz, number, 0.0} kHz',
    infoChannelsValue: '{count, plural, =1 {单声道} =2 {立体声} other {# 声道}}',
    infoPhiraOk: 'Phira 可以播放此文件',
    infoPhiraNo: 'Phira 无法播放此格式',
    infoTitle: '曲名',
    infoArtist: '曲师',
    infoUnknown: '未知',
    infoEdit: '编辑曲目信息',
    infoMp3: 'MP3 会加入一小段解码延迟，且不同播放器的延迟不同。OGG 或 WAV 能保持时间精确。',
    infoStreaming: '此曲目较长，因此从文件播放：时间视图会略不精确。',
    overviewLabel: '带节拍网格的曲目波形',
    overviewRoleDescription: '波形',
    overviewHelp: '单击一个强拍可将第 0 拍放在那里，或拖动网格。顶部的条带用于跳转。Ctrl+滚轮缩放，滚轮滚动。',
    overviewHover: '{time} · 第 {beat} 拍',
    overviewBeatZero: '第 0 拍',
    overviewZoomIn: '放大',
    overviewZoomOut: '缩小',
    overviewZoomFit: '显示整首曲目',
    overviewSetBeatZero: '第 0 拍已移到 {sec, number, 0.000} 秒。',
    overviewDragged: '第 0 拍现在位于 {sec, number, 0.000} 秒。',
    alignmentTitle: '逐小节',
    alignmentHelp:
      '每个小节一行。速度正确时，节拍会排成笔直的竖列。悬停可试听，单击可跳转，Alt+滚轮可将速度微调 0.01。',
    alignmentLabel: '曲目的逐小节对齐',
    alignmentRows: '行数',
    alignmentLock: '锁定区域',
    alignmentLocked: '已锁定到第 {from} 到 {to} 小节',
    alignmentUnlock: '解锁',
    alignmentFollow: '行跟随播放头',
    alignmentScrub: '悬停时试听',
    alignmentBar: '第 {bar} 小节',
    alignmentReadout: '第 {bar} 小节 · {time}',
    alignmentDriftLeft: '竖列向左倾斜：速度可能稍低。',
    alignmentDriftRight: '竖列向右倾斜：速度可能稍高。',
    alignmentStraight: '竖列是直的：速度吻合。',
    alignmentNoData: '还没有可比较的节拍。',
    alignmentPrev: '前面的小节',
    alignmentNext: '后面的小节',
    playWithClicks: '带节拍声播放',
    stopClicks: '停止',
    byEarQuestion: '节拍声落在拍子上了吗？',
    tooEarly: '太早',
    justRight: '刚好',
    tooLate: '太晚',
    tooEarlyTip: '节拍声在拍子之前：将第 0 拍推迟 {ms} 毫秒',
    tooLateTip: '节拍声在拍子之后：将第 0 拍提前 {ms} 毫秒',
    justRightTip: '节拍声落在拍子上：确认时间设置',
    byEarStep: '每按一次，第 0 拍移动 {ms} 毫秒',
    byEarReset: '以 10 毫秒步长重新开始',
    byEarHeadphones: '凭耳朵调整不受耳机影响。',
    byEarConfirmed: '时间设置已确认。之后仍可随时微调。',
    latencyLabel: '音频延迟：',
    latencyValue: '已校准 {ms, number, integer} 毫秒',
    latencyNone: '未校准',
    calibrate: '校准',
    calibrateTip: '测量扬声器或耳机的延迟',
    calTitle: '校准音频延迟',
    calIntro:
      '扬声器，尤其是无线耳机，播放声音会稍有延迟。跟着节拍声敲击，让音符与你听到的声音对齐。这只会改变你看到的内容，永远不会改变谱面。',
    calHow: '每听到一声节拍就按任意键（或单击此处）。前 4 声是预备拍。',
    calStart: '开始',
    calAgain: '重新测量',
    calCountIn: '准备：{n}',
    calTapping: '跟着敲击：第 {n} 声，共 {total} 声',
    calResult: '测得的延迟：{ms, number, integer} 毫秒',
    calResultHelp: '当前的校准值为 {ms, number, integer} 毫秒。',
    calSave: '使用此值',
    calCancel: '取消',
    calClose: '关闭',
    calDetected: '浏览器报告的输出延迟为 {ms, number, integer} 毫秒。',
    calDetectedUnknown: '浏览器没有报告此设备的输出延迟。',
    calSaved: '音频延迟校准已设为 {ms, number, integer} 毫秒。',
    calFailed: '校准失败：{error}',
    calResetValue: '重置为 0 毫秒',
    hintBanner: '你的音频设备会使声音延迟约 {ms, number, integer} 毫秒。请进行校准，让音符对齐。',
    hintBannerUnknown: '浏览器没有报告你的音频设备的延迟。请进行校准，让音符对齐。',
    hintCalibrate: '校准',
    hintDismiss: '关闭',
    stepTitle: '节拍',
    stepIntro: '两个可以听出来的问题：节拍有多快，第一拍在哪里？',
    stepDetected: '检测到 {bpm, number, 0.0} BPM，第 0 拍位于 {sec, number, 0.000} 秒。请试听并调整。',
    stepDetecting: '正在后台检测速度',
    stepNoSong: '请先选择曲目。',
    stepSkipNote:
      '跳过此步将保持 {bpm, number, 0.##} BPM 和第 0 拍位于 {sec, number, 0.###} 秒，且不确认它们，谱面会提醒你检查时间设置。',
    stepClickHint: '在波形中单击一个强拍，可将第 0 拍放在那里。',
    stepEngineBusy: '请关闭打开的谱面，以便在这里试听这首曲目。',
    cmdCalibrate: '校准音频延迟',
    cmdDetectTempo: '检测速度',
    cmdDetectOffset: '检测第一拍',
    cmdTap: '敲击得出速度',
    cmdTapReset: '重置敲击速度',
    cmdTapUse: '使用敲击得出的速度',
    cmdAddTempo: '在播放头处添加速度变化',
    cmdAddMeter: '在播放头处添加拍号变化',
    cmdOffsetEarlier: '将第 0 拍提前 1 毫秒',
    cmdOffsetLater: '将第 0 拍推迟 1 毫秒',
    cmdMultiply: '速度乘以倍数',
    cmdMultiplyArg: '倍数',
    cmdPlayClicks: '带节拍声播放',
    cmdTooEarly: '节拍声太早',
    cmdJustRight: '节拍声刚好',
    cmdTooLate: '节拍声太晚',
    cmdConfirm: '确认时间设置',
    cmdFold: '将 Phira 谱面单独调整并入谱面偏移',
    cmdPolicy: '速度变化时',
    cmdBeatZeroHere: '将第 0 拍放在指针下方',
    noProjectReason: '请先打开一个项目。',
    noSongReason: '此谱面没有曲目。',
    noTapReason: '请先至少敲击 4 次。',
    nothingToFold: '没有谱面单独调整。',
    noBpmReason: '没有可修改的速度。',
    txBpm: '将速度设为 {bpm, number, 0.00} BPM',
    txAddBpm: '在第 {beat} 拍添加速度变化',
    txDeleteBpm: '删除第 {beat} 拍的速度变化',
    txMoveBpm: '将速度变化移到第 {beat} 拍',
    txOffset: '将第 0 拍设为 {sec, number, 0.000} 秒',
    txFold: '并入谱面单独调整',
    txConfirm: '确认时间设置',
    txUnconfirm: '将时间设置标记为未确认',
    txMeter: '在第 {beat} 拍设置每小节 {beats} 拍',
    txAddMeter: '在第 {beat} 拍添加拍号变化',
    txDeleteMeter: '删除第 {beat} 拍的拍号变化',
    txInsert: '插入 {beats} 拍',
    txRemove: '移除第 {from} 到 {to} 拍',
    setScrub: '悬停在逐小节视图上时试听',
    setScrubDesc: '播放指针下方的一小段曲目。',
    setRows: '逐小节视图中显示的小节数',
    setAutoDetect: '打开“曲目与时间”工作区时检测速度',
    setAutoDetectDesc: '每首曲目运行一次；结果随项目保存。',
    helpTimingTitle: '设置速度和第一拍',
    helpTimingBody:
      '时间设置就是两个可以听出来的问题。**节拍有多快？**使用“检测”、按 B 跟着敲击，或直接输入 BPM；速度正确时，逐小节视图会显示笔直的竖列。**第一拍在哪里？**在总览中单击一个强拍或使用“检测”，然后按“带节拍声播放”，并回答“太早”“刚好”或“太晚”，直到节拍声落在拍子上。',
    helpLatencyTitle: '音频延迟',
    helpLatencyBody:
      '无线耳机可能使声音延迟 150 到 300 毫秒。请在“设置”或“曲目与时间”中校准延迟，让你看到的与听到的对齐。校准永远不会改变谱面；移动第 0 拍才会。',
    helpMeterTitle: '拍号变化',
    helpMeterBody:
      '每个速度变化都有每小节拍数，你也可以只改变拍号而不改变速度。小节线、Page Up / Page Down、节拍器重音和逐小节视图都会跟随拍号，因此 3/4 或 7/8 段落也能保持正确。',
    alignmentRowsFit: '适应',
    policyKeepBeatsShort: '保持拍数',
    policyKeepTimeShort: '保持时间',
    setRowsDesc: '0 表示按视图空间尽可能多地显示小节。',
    stepOffsetLabel: '第 0 拍（毫秒）',
    labTitle: '音画同步实验室',
    labBack: '返回谱面库',
    labIntro: '检查声音和画面在此设备上的对齐程度。每次测量都会记入底部的日志；请保存它并随报告一起发送。',
    labFlashTitle: '节拍声与闪烁',
    labFlashHelp:
      '节拍器以 120 BPM 播放，方块会在每一拍亮起。用慢动作拍摄屏幕和扬声器（或使用光传感器和麦克风）：闪烁与节拍声之间的间隔就是剩余的延迟。校准后请比较两种播放路径。',
    labPath: '播放路径',
    labPathWebAudio: 'Web Audio（1x）',
    labPathMedia: '媒体元素（保持音高，0.75x）',
    labStart: '开始',
    labStop: '停止',
    labBusy: '请等待当前测量完成。',
    labCloseChart: '请先关闭打开的谱面：实验室需要单独使用音频引擎。',
    labTapTitle: '跟着敲击',
    labTapHelp: '在 4 声预备拍之后，在接下来的 16 声节拍上各按一次任意键。结果是在当前校准下你仍能听出的延迟。',
    labTapStart: '通过敲击测量',
    labTapResult: '剩余延迟：{ms, number, integer} 毫秒',
    labDecodeTitle: '解码器检查',
    labDecodeHelp:
      '选择同一节拍器的多种格式（WAV、MP3、OGG、FLAC、M4A、Opus）。会找出每个文件中的第一声节拍；其中有 WAV 时，其他文件会与它比较。没有 LAME 标签的 MP3 会晚约 25 毫秒开始。',
    labDecodePick: '选择音频文件',
    labColFile: '文件',
    labColOnset: '第一声节拍（毫秒）',
    labColVsWav: '相对于 WAV（毫秒）',
    labColMp3: 'MP3 标签',
    labLogTitle: '日志（{count, plural, other {# 条}}）',
    labLogEmpty: '还没有测量任何内容。',
    labLogCopy: '复制日志',
    labLogSave: '保存日志',
    labLogClear: '清除',
    labLogCopied: '日志已复制到剪贴板。',
  },
};

export default catalog;
